import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import { SmsActions } from '../../Services/_actions/SMS/sms.actions.ts';
import LoadingButton from '../Shared/Loading/index.tsx';

const CreateEditSms: React.FC = () => {
    const navigate = useNavigate();
    const actions = SmsActions();
    const location = useLocation();
    const editingSms = location.state?.sms;
    const { showSnackBar } = useSnackBar();
    const [loading, setLoading] = useState(false);
    const [errors,setErrors] = useState({name:"",content:"",entityId:"",headers:"",isUnicode:""});


    // Initialize form state with either empty values or the SMS data (if editing)
    const [formState, setFormState] = useState({
        name: editingSms?.name || '',
        content: editingSms?.content || '',
        entityId: editingSms?.dlt_entity_id || '',
        headers: editingSms?.headers ? editingSms.headers.join(', ') : '',
        isUnicode: editingSms?.isunicode || false,
    });
    const validateForm = () => {
        let errors:any = {};
    
        if (!formState?.name?.trim()) {
            errors.name = 'Name is required';
        }
        if (!formState.content.trim()) {
            errors.content = 'Content is required';
        }
        if (!formState.entityId.trim()) {
            errors.entityId = 'Entity ID is required';
        }
        if (!formState.headers.trim()) {
            errors.headers = 'Headers are required';
        } else {
            const headersArray = formState?.headers?.split(',').map(header => header?.trim());
            if (headersArray?.some(header => header === '')) {
                errors.headers = 'Headers cannot contain empty values';
            }
        }
        setErrors(errors);
        return Object.keys(errors).length===0;
    };
    

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            showSnackBar('All fields are required', 'error'); // Updated to show SnackBar notification
            return;
        }

        const payload = {
            name: formState?.name,
            content: formState?.content,
            headers: formState?.headers.split(',').map((header) => header?.trim()),
            dlt_entity_id: formState?.entityId,
            isUnicode: formState?.isUnicode,
        };

        try {
            let response;
            if (editingSms) {
                response = await actions.updateSms(payload);
            } else {
                response = await actions.createSms(payload);
            }

            if (response?.status === 200) {
                if(!response?.data?.data?.success && response?.data?.data?.errors?.length>0){
                    let errorMessage = response?.data?.data?.errors?.map(err => err?.message).join(', ');
                    showSnackBar(errorMessage || `Failed to ${editingSms ? 'update' : 'create'} SMS template`, 'error');
                } else {
                    showSnackBar(`SMS template ${editingSms ? 'updated' : 'created'} successfully`, 'success'); // Updated
                    navigate('/r/sms/list');
                }
            } 
        } catch (error) {
            showSnackBar(error?.toString() || 'An unexpected error occurred', 'error'); // Updated
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                padding: "20px",
                maxWidth: "900px",
                margin: "auto",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
        >
            <Typography
                variant="h5"
                component="h1"
                sx={{
                    marginBottom: "20px",
                    textAlign: "center",
                    fontWeight: "bold",
                }}
            >
                {editingSms ? 'Update SMS Template' : 'Create SMS Template'}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={
                            <Typography component="span">
                                Template Name <span style={{ color: 'red' }}>*</span>
                            </Typography>
                        }
                        fullWidth
                        value={formState?.name}
                        size="small"
                        onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                        error={!!errors?.name}
                        helperText={errors?.name}            
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={
                            <Typography component="span">
                                Headers (comma-separated) <span style={{ color: 'red' }}>*</span>
                            </Typography>
                        }
                        size="small"
                        value={formState?.headers || ''}
                        onChange={(e) => setFormState({ ...formState, headers: e.target.value })}
                        error={!!errors?.headers}
                        helperText={errors?.headers}            
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={
                            <Typography component="span">
                                Entity ID<span style={{ color: 'red' }}>*</span>
                            </Typography>
                        }
                        size="small"
                        value={formState?.entityId}
                        onChange={(e) => setFormState({ ...formState, entityId: e.target.value })}
                        error={!!errors?.entityId}
                        helperText={errors?.entityId}            

                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={
                            <Typography component="span">
                                Content<span style={{ color: 'red' }}>*</span>
                            </Typography>
                        }
                        size="small"
                        multiline
                        rows={4}
                        value={formState?.content}
                        onChange={(e) => setFormState({ ...formState, content: e.target.value })}
                        placeholder="Example: Welcome from {#var1#}. This is a Test Message"
                        error={!!errors?.content}
                        helperText={errors?.content}            

                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" marginTop="3px">
                <Grid item xs={12} sm={6} sx={{ marginTop: "-113px" }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formState.isUnicode}
                                onChange={(e) => setFormState({ ...formState, isUnicode: e.target.checked })}
                            />
                        }
                        label="Unicode"
                    />
                </Grid>
                <Grid item xs={12} sm={6} container justifyContent="flex-end">
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <LoadingButton
                            buttonName="Cancel"
                            variant="outlined"
                            onClickButton={() => navigate('/r/sms/list')}
                        />
                        <LoadingButton
                            buttonName={`${editingSms ? 'Update' : 'Submit'}`}
                            onClickButton={handleSubmit}
                            isLoading={loading}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CreateEditSms;
